import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { MdCallMerge, MdClose, MdOutlineOpenInNew } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { STAFF, SUGGESTIONS } from "../../config/apiConfig";
import { useApplication } from "../../contexts/ApplicationContext";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { Badge, ErrorBlock, SkeletonRows } from "./Primitives";
import { PLAN_TONE } from "./crmHelpers";
import {
  describeDemand,
  describeEvent,
  formatDateTime,
  SUGGESTION_AREA_LABELS,
  SUGGESTION_KIND_LABELS,
  SUGGESTION_STATUS_META,
  SUGGESTION_STATUS_ORDER,
  summarizeHistory,
} from "./suggestionHelpers";
import ClientLink from "./ClientLink";

/*
  Painel lateral de uma sugestão.

  Reúne o que a equipe precisa para decidir sem sair da lista: o texto e as
  fotos, quem pediu (e quem mais pediu a mesma coisa), quanto essas contas
  pagam, e a decisão (status + resposta).

  Três regras que não aparecem na tela:

  - **Decide-se na principal.** Uma sugestão juntada a outra mostra o aviso e o
    caminho para a principal; o status e a resposta dela são cópias.
  - **Juntar e decidir são de ADMIN.** O gerente de contas lê (só a carteira
    dele), então os controles somem para ele em vez de falhar com 403.
  - **Reprovar exige o motivo.** Quem enviou lê essa resposta no app; "Reprovada"
    sem explicação é a pior resposta que uma cliente pode receber.
*/

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const Section = ({ title, children }) => (
  <section className="mt-5">
    <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
      {title}
    </h3>
    {children}
  </section>
);

const PhotoGrid = ({ photos }) =>
  photos?.length ? (
    <div className="mt-2 grid grid-cols-4 gap-2">
      {photos.map((photo) => (
        <a
          key={photo.id}
          href={photo.url}
          target="_blank"
          rel="noreferrer"
          className="block aspect-square overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
        >
          <img src={photo.url} alt="" className="h-full w-full object-cover" />
        </a>
      ))}
    </div>
  ) : null;

const Requester = ({ item }) => (
  <p className="break-words text-xs text-slate-500">
    {item.company ? (
      <ClientLink
        to={`/clientes/${item.company.id}`}
        className="font-semibold text-slate-700 hover:text-pink-700"
      >
        {item.company.name}
      </ClientLink>
    ) : (
      "Conta removida"
    )}
    {item.professional?.name ? ` · ${item.professional.name}` : ""}
    {` · ${formatDateTime(item.createdAt)}`}
  </p>
);

/* ---------------------------------------------------------- juntar com outra */

const MergePicker = ({ currentId, onPick, isBusy }) => {
  const [term, setTerm] = useState("");
  const search = useDebouncedValue(term, 350);

  const { data, isFetching } = useQuery(
    ["suggestions", "picker", search],
    async () => {
      const params = new URLSearchParams({ itemsPerPage: "8" });
      if (search.trim()) params.set("search", search.trim());
      const { data: response } = await axios.get(
        `${SUGGESTIONS.LIST}?${params.toString()}`
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  const options = (data?.suggestions || []).filter(
    (item) => item.id !== currentId
  );

  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <input
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder="Buscar a sugestão principal pelo texto ou salão"
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
      />

      <div className="mt-2 max-h-56 space-y-1 overflow-y-auto">
        {options.map((item) => (
          <button
            key={item.id}
            type="button"
            disabled={isBusy}
            onClick={() => onPick(item)}
            className="block w-full rounded-lg px-2.5 py-2 text-left transition hover:bg-pink-50 disabled:opacity-50"
          >
            <span className="block truncate text-sm font-semibold text-slate-800">
              #{item.id} · {item.message}
            </span>
            <span className="block truncate text-[11px] text-slate-500">
              {item.company?.name || "Conta removida"}
              {item.group ? ` · ${describeDemand(item.group)}` : ""}
            </span>
          </button>
        ))}
        {!options.length ? (
          <p className="px-2 py-3 text-center text-xs text-slate-400">
            {isFetching ? "Buscando..." : "Nenhuma sugestão encontrada."}
          </p>
        ) : null}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------- painel */

const SuggestionDrawer = ({ suggestionId, onClose, onOpen }) => {
  const { isAdminOrOperator } = useApplication();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState("PENDING");
  const [response, setResponse] = useState("");
  const [implementerId, setImplementerId] = useState("");
  const [showMerge, setShowMerge] = useState(false);
  const [actionError, setActionError] = useState("");
  const [pushResult, setPushResult] = useState(null);

  const detailQuery = useQuery(["suggestion", suggestionId], async () => {
    const { data } = await axios.get(`${SUGGESTIONS.ONE}/${suggestionId}`);
    return data.data;
  });
  const detail = detailQuery.data;

  // Quem pode ser indicado como autor da implementação: a equipe do painel.
  const { data: staffOptions } = useQuery(
    ["staff", "options"],
    async () => {
      const { data } = await axios.get(STAFF.OPTIONS);
      return data.data;
    },
    { enabled: isAdminOrOperator }
  );

  // Abrir outra sugestão (ou salvar) refaz o rascunho a partir do que o
  // servidor guarda.
  useEffect(() => {
    if (!detail) return;
    setStatus(detail.status);
    setResponse(detail.response || "");
    setImplementerId(detail.implementedBy?.id ? String(detail.implementedBy.id) : "");
    setShowMerge(false);
    setActionError("");
  }, [detail?.id, detail?.status, detail?.response, detail?.implementedBy?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPushResult(null);
  }, [suggestionId]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // No celular o painel ocupa a tela toda: sem isto, rolar o texto arrasta a
  // lista que está por trás.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Decidir uma principal muda também as juntadas a ela: tudo que mostra
  // sugestão precisa reler.
  const refresh = (fresh) => {
    if (fresh?.id) queryClient.setQueryData(["suggestion", fresh.id], fresh);
    queryClient.invalidateQueries(["suggestions"]);
    queryClient.invalidateQueries(["suggestion"]);
  };

  const decide = useMutation(
    () =>
      axios.put(`${SUGGESTIONS.ONE}/${suggestionId}`, {
        status,
        response,
        // Só faz sentido em "implementada"; fora dela o servidor limpa.
        ...(status === "IMPLEMENTED"
          ? { implementerUserId: implementerId ? Number(implementerId) : null }
          : {}),
      }),
    {
      onMutate: () => {
        setActionError("");
        setPushResult(null);
      },
      onSuccess: ({ data }) => {
        setPushResult(data.data.push || null);
        refresh(data.data);
      },
      onError: (error) =>
        setActionError(errorMessage(error, "Não foi possível salvar.")),
    }
  );

  const merge = useMutation(
    (targetId) =>
      axios.post(`${SUGGESTIONS.ONE}/${suggestionId}/merge`, { targetId }),
    {
      onMutate: () => setActionError(""),
      onSuccess: ({ data }) => {
        refresh(data.data);
        // A sugestão aberta virou juntada: segue para a principal.
        onOpen?.(data.data.id);
      },
      onError: (error) =>
        setActionError(errorMessage(error, "Não foi possível juntar.")),
    }
  );

  const unmerge = useMutation(
    (id) => axios.delete(`${SUGGESTIONS.ONE}/${id}/merge`),
    {
      onMutate: () => setActionError(""),
      onSuccess: ({ data }) => refresh(data.data),
      onError: (error) =>
        setActionError(errorMessage(error, "Não foi possível desfazer.")),
    }
  );

  const isMerged = Boolean(detail?.mergedIntoId);
  const savedImplementerId = detail?.implementedBy?.id
    ? String(detail.implementedBy.id)
    : "";
  const dirty =
    detail &&
    (status !== detail.status ||
      response.trim() !== (detail.response || "") ||
      (status === "IMPLEMENTED" && implementerId !== savedImplementerId));
  const needsReason = status === "REJECTED" && !response.trim();
  const statusMeta = SUGGESTION_STATUS_META[detail?.status];

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />

      {/* `inset-y-0` e não `h-screen`: no navegador do celular 100vh é a altura
          com a barra de endereço recolhida e cortaria o fim do painel. */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[520px] flex-col bg-white shadow-2xl">
        <header className="flex items-start gap-3 border-b border-slate-200 px-4 py-4 sm:px-5">
          <div className="min-w-0 flex-1">
            {detail ? (
              <>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge tone="neutral">
                    {SUGGESTION_KIND_LABELS[detail.kind] || "Sugestão"}
                  </Badge>
                  {detail.area ? (
                    <Badge tone="neutral">
                      Aba: {SUGGESTION_AREA_LABELS[detail.area] || detail.area}
                    </Badge>
                  ) : null}
                  {statusMeta ? (
                    <Badge tone={statusMeta.tone}>{statusMeta.label}</Badge>
                  ) : null}
                  <span className="text-xs text-slate-400">#{detail.id}</span>
                </div>
                <div className="mt-2">
                  <Requester item={detail} />
                </div>
              </>
            ) : (
              <p className="text-sm font-semibold text-slate-500">
                Sugestão #{suggestionId}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fechar (Esc)"
            aria-label="Fechar"
            className="-m-1 shrink-0 rounded-lg p-2.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 sm:m-0 sm:p-1.5"
          >
            <MdClose size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-8 pt-1 sm:px-5">
          {detailQuery.isLoading ? (
            <SkeletonRows rows={5} className="mt-5" />
          ) : detailQuery.error ? (
            <div className="mt-5">
              <ErrorBlock
                message={errorMessage(
                  detailQuery.error,
                  "Não foi possível carregar a sugestão."
                )}
                onRetry={detailQuery.refetch}
              />
            </div>
          ) : detail ? (
            <>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {detail.company ? (
                  <>
                    <Badge tone={PLAN_TONE[detail.company.planId] || "neutral"}>
                      {detail.company.planId}
                    </Badge>
                    <ClientLink
                      hideWhenBlocked
                      to={`/clientes/${detail.company.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-pink-700"
                    >
                      Abrir ficha da conta <MdOutlineOpenInNew size={13} />
                    </ClientLink>
                  </>
                ) : null}
                {detail.planAtSend &&
                detail.planAtSend !== detail.company?.planId ? (
                  <span className="text-[11px] text-slate-400">
                    Estava no plano {detail.planAtSend} ao enviar
                  </span>
                ) : null}
              </div>

              {isMerged ? (
                <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50 p-3">
                  <p className="text-sm font-semibold text-sky-900">
                    Juntada à sugestão #{detail.mergedIntoId}
                  </p>
                  <p className="text-xs text-sky-800">
                    O status e a resposta vêm da principal. Para mudar, abra a
                    principal.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => onOpen?.(detail.mergedIntoId)}
                      className="rounded-lg bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-800"
                    >
                      Abrir a principal
                    </button>
                    {isAdminOrOperator ? (
                      <button
                        type="button"
                        disabled={unmerge.isLoading}
                        onClick={() => unmerge.mutate(detail.id)}
                        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-sky-800 ring-1 ring-inset ring-sky-300 hover:bg-white disabled:opacity-50"
                      >
                        Desfazer a junção
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <Section title="O que ela sugeriu">
                <p className="whitespace-pre-wrap break-words text-sm text-slate-900">
                  {detail.message}
                </p>
                {detail.benefit ? (
                  <p className="mt-2 whitespace-pre-wrap break-words text-xs text-slate-500">
                    <span className="font-semibold text-slate-600">
                      Como ajudaria:
                    </span>{" "}
                    {detail.benefit}
                  </p>
                ) : null}
                <PhotoGrid photos={detail.photos} />
              </Section>

              {!isMerged && detail.group ? (
                <Section title="Demanda">
                  <div className="rounded-xl bg-slate-50 px-3 py-2.5">
                    <p className="text-sm font-bold text-slate-900">
                      {describeDemand(detail.group)}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      Cada conta é contada uma vez, mesmo que mais de uma
                      pessoa dela tenha pedido.
                    </p>
                  </div>
                </Section>
              ) : null}

              {detail.merged?.length ? (
                <Section title={`Quem mais pediu (${detail.merged.length})`}>
                  <ul className="space-y-2">
                    {detail.merged.map((item) => (
                      <li
                        key={item.id}
                        className="rounded-xl border border-slate-200 p-3"
                      >
                        <Requester item={item} />
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800">
                          {item.message}
                        </p>
                        {item.benefit ? (
                          <p className="mt-1 text-xs text-slate-500">
                            Como ajudaria: {item.benefit}
                          </p>
                        ) : null}
                        <PhotoGrid photos={item.photos} />
                        {isAdminOrOperator ? (
                          <button
                            type="button"
                            disabled={unmerge.isLoading}
                            onClick={() => unmerge.mutate(item.id)}
                            className="mt-1 py-1.5 text-[11px] font-semibold text-slate-400 underline hover:text-rose-600 disabled:opacity-50 sm:mt-2 sm:py-0"
                          >
                            Desfazer a junção
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </Section>
              ) : null}

              {!isMerged ? (
                <Section title="Decisão">
                  {isAdminOrOperator ? (
                    <>
                      {/* Grade 2×2 no celular (alvo de toque maior); de `sm` em
                          diante, a fileira de pílulas de sempre. */}
                      <div className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap">
                        {SUGGESTION_STATUS_ORDER.map((value) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setStatus(value)}
                            className={`rounded-full px-3 py-2 text-xs font-semibold ring-1 ring-inset transition sm:py-1 ${
                              status === value
                                ? "bg-pink-600 text-white ring-pink-600"
                                : "bg-white text-slate-600 ring-slate-200 hover:ring-pink-200"
                            }`}
                          >
                            {SUGGESTION_STATUS_META[value].label}
                          </button>
                        ))}
                      </div>

                      {status === "IMPLEMENTED" ? (
                        <label className="mt-3 block">
                          <span className="text-xs font-semibold text-slate-600">
                            Quem implementou (opcional)
                          </span>
                          <select
                            value={implementerId}
                            onChange={(event) => setImplementerId(event.target.value)}
                            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                          >
                            <option value="">Não informado</option>
                            {(staffOptions || []).map((person) => (
                              <option key={person.id} value={person.id}>
                                {person.name}
                              </option>
                            ))}
                          </select>
                          <span className="block text-[11px] text-slate-400">
                            Pode ser outra pessoa além de quem está marcando.
                            Só a equipe vê, a cliente não.
                          </span>
                        </label>
                      ) : null}

                      <textarea
                        value={response}
                        onChange={(event) => setResponse(event.target.value)}
                        rows={4}
                        maxLength={2000}
                        placeholder={
                          status === "REJECTED"
                            ? "Explique o motivo. Quem enviou lê isto no app."
                            : "Resposta para quem enviou (opcional). Aparece no app."
                        }
                        className="mt-2 w-full resize-y rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                      />

                      {status === "IMPLEMENTED" &&
                      detail.status !== "IMPLEMENTED" ? (
                        <p className="text-xs text-slate-500">
                          Ao salvar, quem pediu recebe um aviso no celular.
                        </p>
                      ) : null}
                      {detail.merged?.length ? (
                        <p className="text-xs text-slate-500">
                          Vale também para as {detail.merged.length}{" "}
                          sugestões juntadas a esta.
                        </p>
                      ) : null}
                      {needsReason ? (
                        <p className="text-xs font-semibold text-rose-600">
                          Escreva o motivo para poder reprovar.
                        </p>
                      ) : null}
                      {detail.respondedByName ? (
                        <p className="text-[11px] text-slate-400">
                          Última decisão: {detail.respondedByName} em{" "}
                          {formatDateTime(detail.respondedAt)}
                        </p>
                      ) : null}

                      <button
                        type="button"
                        onClick={() => decide.mutate()}
                        disabled={!dirty || needsReason || decide.isLoading}
                        className="mt-2 w-full rounded-xl bg-pink-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-pink-700 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:py-2"
                      >
                        {decide.isLoading ? "Salvando..." : "Salvar decisão"}
                      </button>

                      {pushResult ? (
                        <div className="mt-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
                          {pushResult.sent > 0 ? (
                            <p className="font-semibold text-emerald-700">
                              Aviso enviado para {pushResult.sent}{" "}
                              {pushResult.sent === 1 ? "pessoa" : "pessoas"}.
                            </p>
                          ) : null}
                          {pushResult.noToken > 0 ? (
                            <p>
                              {pushResult.noToken}{" "}
                              {pushResult.noToken === 1 ? "pessoa" : "pessoas"}{" "}
                              sem notificação ativa (só recebe quem tem o app
                              instalado no celular). Salvar de novo depois
                              tenta avisar.
                            </p>
                          ) : null}
                          {pushResult.failed > 0 ? (
                            <p className="text-rose-600">
                              O envio falhou para {pushResult.failed}{" "}
                              {pushResult.failed === 1 ? "pessoa" : "pessoas"}.
                              Confira a configuração do Firebase no servidor.
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-slate-700">
                        {statusMeta?.label}
                        {detail.response ? `: ${detail.response}` : ""}
                      </p>
                      <p className="mt-1 text-[11px] text-slate-400">
                        Só administradores decidem sugestões.
                      </p>
                    </>
                  )}
                </Section>
              ) : null}

              <Section title="Histórico interno">
                {summarizeHistory(detail) ? (
                  <p className="mb-2 text-sm font-semibold text-slate-800">
                    {summarizeHistory(detail)}
                  </p>
                ) : null}
                <ol className="space-y-1.5 border-l-2 border-slate-100 pl-3">
                  <li className="text-xs text-slate-500">
                    <span className="text-slate-400">
                      {formatDateTime(detail.createdAt)}
                    </span>{" "}
                    Enviada por {detail.professional?.name || "alguém"}
                    {detail.company ? ` (${detail.company.name})` : ""}
                  </li>
                  {(detail.history || []).map((event) => (
                    <li key={event.id} className="text-xs text-slate-700">
                      <span className="text-slate-400">
                        {formatDateTime(event.createdAt)}
                      </span>{" "}
                      {describeEvent(event)}
                    </li>
                  ))}
                </ol>
                <p className="mt-2 text-[11px] text-slate-400">
                  Só a equipe da Relinq vê este histórico. A cliente do salão
                  nunca vê.
                </p>
              </Section>

              {actionError ? (
                <div className="mt-3">
                  <ErrorBlock message={actionError} />
                </div>
              ) : null}

              {isAdminOrOperator && !isMerged ? (
                <Section title="Parecida com outra?">
                  {showMerge ? (
                    <>
                      <p className="mb-2 text-xs text-slate-500">
                        Escolha a sugestão principal. Esta (e as que já estão
                        juntadas a ela) passam a contar como pedidos da
                        principal e ganham o status dela.
                      </p>
                      <MergePicker
                        currentId={detail.id}
                        isBusy={merge.isLoading}
                        onPick={(item) => merge.mutate(item.id)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowMerge(false)}
                        className="mt-1 py-1.5 text-xs font-semibold text-slate-400 underline sm:mt-2 sm:py-0"
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowMerge(true)}
                      className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 ring-1 ring-inset ring-slate-200 transition hover:ring-pink-300"
                    >
                      <MdCallMerge size={16} /> Juntar com outra sugestão
                    </button>
                  )}
                </Section>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default SuggestionDrawer;
